import React, { createContext, useCallback, useContext, useMemo, useState, lazy, Suspense } from 'react';
import type { TraceResult } from '../../services/tracking.service';

const TraceResultModal = lazy(() => import('./TraceResultModal'));

/**
 * Page-level owner of the trace result modal.
 *
 * Exists because a trace launched from a TABLE CELL cannot own its own modal. A
 * function cell renderer is a component TYPE to React (see TanTable's flexRender
 * note), so a grid that rebuilds its column definitions on render — the LC bucket
 * tables call generateBucketColumns inline — gives every cell a new type on every
 * render and React unmounts the old one. The trace's own row refresh is itself a
 * re-render, so the cell that opened the modal was torn down a moment later: the
 * row updated correctly and the modal never appeared.
 *
 * Hoisting the result out of the cell makes the modal immune to row and column
 * churn — the table can rebuild as often as it likes underneath it.
 *
 * Opt-in by design: TraceButton falls back to its own local modal when no provider
 * is mounted, so /pro's per-field trace buttons keep working exactly as they did.
 */
interface TraceResultsContextValue {
  show: (title: string, results: TraceResult[]) => void;
}

const TraceResultsContext = createContext<TraceResultsContextValue | null>(null);

export const TraceResultsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState<{ title: string; results: TraceResult[] } | null>(null);

  const show = useCallback((title: string, results: TraceResult[]) => {
    setOpen({ title, results });
  }, []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <TraceResultsContext.Provider value={value}>
      {children}
      {open && (
        // No fallback: the modal is all that's awaited and it arrives in a tick —
        // a spinner would flash for less time than it takes to read.
        <Suspense fallback={null}>
          <TraceResultModal
            open
            title={open.title}
            results={open.results}
            onClose={() => setOpen(null)}
          />
        </Suspense>
      )}
    </TraceResultsContext.Provider>
  );
};

/** Null when no provider is mounted, which is the signal to fall back to a local
 *  modal rather than silently swallowing the result. */
export const useTraceResults = (): TraceResultsContextValue | null =>
  useContext(TraceResultsContext);
